import "./styles.scss";

import { Icons } from "components";
import { Input } from "antd";
import React from "react";
import Strings from "utils/strings";

const Password = ({
	className,
	password,
	showPassword,
	onPasswordChange,
	onShowPasswordChange,
	placeholder
}: {
	className: string;
	password: string;
	showPassword: string;
	onPasswordChange: (password: string, isValid: boolean) => void; // Updated type
	onShowPasswordChange: Function;
	placeholder: string;
}) => {
	// Password validation criteria
	const hasMinLength = password.length >= 8;
	const hasUpperCase = /[A-Z]/.test(password);
	const hasLowerCase = /[a-z]/.test(password);
	const hasNumber = /\d/.test(password);
	const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

	// Calculate password strength score (0-5) based on number of requirements met
	const calculatePasswordScore = (pwd: string): number => {
		if (pwd === "") return 0;

		let score = 0;
		if (pwd.length >= 8) score++;
		if (/[A-Z]/.test(pwd)) score++;
		if (/[a-z]/.test(pwd)) score++;
		if (/\d/.test(pwd)) score++;
		if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;

		return score;
	};

	const score = calculatePasswordScore(password);

	// Function to validate password criteria
	const isPasswordValid = (pwd: string): boolean => {
		const localHasMinLength = pwd.length >= 8;
		const localHasUpperCase = /[A-Z]/.test(pwd);
		const localHasLowerCase = /[a-z]/.test(pwd);
		const localHasNumber = /\d/.test(pwd);
		const localHasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

		return localHasMinLength && localHasUpperCase && localHasLowerCase && localHasNumber && localHasSpecialChar;
	};

	// Helper function to get status icon and color for requirements
	const getRequirementStatus = (fulfilled: boolean) => {
		return {
			icon: fulfilled ? (
				<span className="requirement-fulfilled-text">
					<Icons.CheckDouble />
				</span>
			) : (
				<span className="requirement-unfulfilled-text">
					<Icons.CheckDouble />
				</span>
			),
			textClass: fulfilled ? "requirement-fulfilled-text" : "requirement-unfulfilled-text"
		};
	};

	return (
		<div className="password-container">
			<Input
				placeholder={placeholder}
				className={className}
				value={password}
				type={showPassword ? "text" : "password"}
				required
				prefix={<Icons.Lock />}
				suffix={
					<button
						className="PasswordInputEye"
						onClick={(e: any) => {
							e.preventDefault();
							e.stopPropagation();
							onShowPasswordChange(!showPassword);
						}}
						title={showPassword ? "Hide password" : "Show password"}
					>
						<Icons.Eye />
					</button>
				}
				onChange={(e: any) => onPasswordChange(e.target.value, isPasswordValid(e.target.value))} // Pass validity as the second argument
			/>
			<div className="password-strength-bar">
				<div className="strength-bar">
					{[0, 1, 2, 3, 4].map((level) => (
						<div
							key={level}
							className={`strength-bar-segment ${
								password === "" // No color if password is empty
									? ""
									: level < score
									? `strength-bar-segment-${score}`
									: ""
							}`}
						></div>
					))}
				</div>
				<div className="strength-label">
					{password && <span className={`strength-text strength-text-${score}`}></span>}
				</div>

				{/* Password requirements list */}
				<div className="password-requirements">
					<div className="requirement">
						{getRequirementStatus(hasMinLength).icon}
						<span className={getRequirementStatus(hasMinLength).textClass}>
							{Strings.password_requirements.minLength}
						</span>
					</div>

					<div className="requirement">
						{getRequirementStatus(hasUpperCase).icon}
						<span className={getRequirementStatus(hasUpperCase).textClass}>
							{Strings.password_requirements.upperCase}
						</span>
					</div>

					<div className="requirement">
						{getRequirementStatus(hasLowerCase).icon}
						<span className={getRequirementStatus(hasLowerCase).textClass}>
							{Strings.password_requirements.lowerCase}
						</span>
					</div>

					<div className="requirement">
						{getRequirementStatus(hasNumber).icon}
						<span className={getRequirementStatus(hasNumber).textClass}>
							{Strings.password_requirements.number}
						</span>
					</div>

					<div className="requirement">
						{getRequirementStatus(hasSpecialChar).icon}
						<span className={getRequirementStatus(hasSpecialChar).textClass}>
							{Strings.password_requirements.specialChar}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Password;
